import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';

export interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterLink, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './app-breadcrumb.component.html',
})
export class AppBreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly breadcrumbs = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.buildBreadcrumbs(this.activatedRoute.root)),
    ),
    { initialValue: [] as Breadcrumb[] },
  );

  private buildBreadcrumbs(
    route: ActivatedRoute,
    url = '',
    crumbs: Breadcrumb[] = [],
  ): Breadcrumb[] {
    // Solo sigue la rama primaria (children[0]) — coincide con el patrón de rutas del proyecto
    // (sin outlets secundarios). Defensivo ante snapshots intermedios durante la navegación.
    const child = route.children[0];
    if (!child?.snapshot) return crumbs;

    const routeURL = child.snapshot.url.map((seg) => seg.path).join('/');
    const nextUrl = routeURL ? `${url}/${routeURL}` : url;
    const label = child.snapshot.data['breadcrumb'] as string | undefined;
    if (label) {
      crumbs.push({ label, url: nextUrl });
    }
    return this.buildBreadcrumbs(child, nextUrl, crumbs);
  }
}
